import React, { useState } from 'react';
import { SienaguardService, SienaguardServiceDetail } from '../../../src/api/sienaguardService';
import { FaExclamationTriangle, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

interface ServicesTableProps {
  services: SienaguardService[];
  serviceDetailsMap: Record<string, SienaguardServiceDetail>;
  onSelectService: (serviceId: string) => void;
  selectedServiceId: string | null;
  onOpenDrawer: (service: SienaguardService) => void;
}

type SortKey = 'domain' | 'start_date' | 'end_date' | 'contact_emails' | 'suspicious_domains';

function formatDateToDDMMYYYY(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

function parseDateForSort(dateStr: string): number {
  if (!dateStr) return 0;
  const [year, month, day] = dateStr.split('-');
  const y = Number(year), m = Number(month) - 1, d = Number(day);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return 0;
  return new Date(y, m, d).getTime();
}

/**
 * Cuenta dominios únicos de typosquatting_info y names_tld_info,
 * excluyendo el original.
 */
function getSuspiciousCount(
  detail: SienaguardServiceDetail | undefined,
  originalDomain: string
): number {
  if (!detail) return 0;
  const fromTypos = detail.typosquatting_info?.map(t => t.fake_domain) ?? [];
  const fromNames = detail.names_tld_info?.map(n => n.domain) ?? [];
  const all = [...fromTypos, ...fromNames];
  const filtered = all.filter(d => d.toLowerCase() !== originalDomain.toLowerCase());
  return new Set(filtered).size;
}

const ServicesTable: React.FC<ServicesTableProps> = ({
  services,
  serviceDetailsMap,
  onSelectService,
  selectedServiceId,
  onOpenDrawer,
}) => {
  const { t } = useTranslation();
  const [hoveredServiceId, setHoveredServiceId] = useState<string | null>(null);

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'start_date',
    direction: 'desc',
  });

  const handleSort = (key: SortKey) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const sortedServices = [...services].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';

    const detailA = serviceDetailsMap[a.id];
    const detailB = serviceDetailsMap[b.id];

    switch (sortConfig.key) {
      case 'domain':
        aVal = a.domain.toLowerCase();
        bVal = b.domain.toLowerCase();
        break;
      case 'start_date':
        aVal = parseDateForSort(a.start_date);
        bVal = parseDateForSort(b.start_date);
        break;
      case 'end_date':
        aVal = parseDateForSort(a.end_date);
        bVal = parseDateForSort(b.end_date);
        break;
      case 'contact_emails':
        aVal = detailA?.contact_email_count || 0;
        bVal = detailB?.contact_email_count || 0;
        break;
      case 'suspicious_domains':
        aVal = getSuspiciousCount(detailA, a.domain);
        bVal = getSuspiciousCount(detailB, b.domain);
        break;
      default:
        aVal = parseDateForSort(a.start_date);
        bVal = parseDateForSort(b.start_date);
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortArrow = (key: SortKey) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <FaChevronUp className="inline-block ml-1 text-xs" />
    ) : (
      <FaChevronDown className="inline-block ml-1 text-xs" />
    );
  };

  return (
    <div className="bg-gradient-to-b from-[#0A1723] to-[#050C12] text-white p-4 rounded-xl shadow mb-8">
      <h2 className="text-xl font-semibold text-white mb-4">
        {t('servicesTable.activeServicesTitle')}
      </h2>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="text-white bg-[#131F2D]">
              <th
                onClick={() => handleSort('domain')}
                className="px-4 py-2 cursor-pointer text-left border-b border-gray-600"
              >
                {t('servicesTable.domain')}
                {renderSortArrow('domain')}
              </th>
              <th
                onClick={() => handleSort('start_date')}
                className="px-4 py-2 cursor-pointer text-left border-b border-gray-600"
              >
                {t('servicesTable.startDate')}
                {renderSortArrow('start_date')}
              </th>
              <th
                onClick={() => handleSort('end_date')}
                className="px-4 py-2 cursor-pointer text-left border-b border-gray-600"
              >
                {t('servicesTable.endDate')}
                {renderSortArrow('end_date')}
              </th>
              <th
                onClick={() => handleSort('contact_emails')}
                className="px-4 py-2 cursor-pointer text-center border-b border-gray-600"
              >
                {t('servicesTable.contactEmails')}
                {renderSortArrow('contact_emails')}
              </th>
              <th
                onClick={() => handleSort('suspicious_domains')}
                className="px-4 py-2 cursor-pointer text-center border-b border-gray-600"
              >
                {t('servicesTable.suspiciousDomains')}
                {renderSortArrow('suspicious_domains')}
              </th>
              <th className="px-4 py-2 text-center border-b border-gray-600">
                {t('servicesTable.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedServices.map(service => {
              const isSelected = selectedServiceId === service.id;
              const isHovered = hoveredServiceId === service.id;
              const detail = serviceDetailsMap[service.id];
              const emailCount = detail?.contact_email_count || 0;
              const suspiciousCount = getSuspiciousCount(detail, service.domain);

              return (
                <tr
                  key={service.id}
                  onClick={() => onSelectService(service.id)}
                  onMouseEnter={() => setHoveredServiceId(service.id)}
                  onMouseLeave={() => setHoveredServiceId(null)}
                  className={`
                    border-b border-gray-600 
                    transition-colors 
                    ${isSelected ? 'bg-[#162536]' : isHovered ? 'bg-[#14202E]' : ''}
                    cursor-pointer
                  `}
                >
                  <td className="px-4 py-2">{service.domain}</td>
                  <td className="px-4 py-2">
                    {formatDateToDDMMYYYY(service.start_date)}
                  </td>
                  <td className="px-4 py-2">
                    {formatDateToDDMMYYYY(service.end_date)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className={`inline-flex items-center gap-1 font-medium ${
                        emailCount === 0 ? 'text-red-400' : 'text-white'
                      }`}
                    >
                      {emailCount}/5
                      {emailCount === 0 && <FaExclamationTriangle />}
                    </span>
                    <br />
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onOpenDrawer(service);
                      }}
                      className="text-white underline mt-1 text-sm"
                    >
                      {t('servicesTable.edit')}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {suspiciousCount > 0 ? (
                      <span
                        className="px-2 py-1 rounded-full text-sm font-medium bg-red-600 text-white"
                        title={t('servicesTable.suspiciousTip')}
                      >
                        {suspiciousCount}
                      </span>
                    ) : (
                      <span className="text-sm text-white font-semibold">0</span>
                    )}
                  </td>
                  <td
                    onClick={e => e.stopPropagation()}
                    className="px-4 py-2 text-center"
                  >
                    <button
                      onClick={() => onOpenDrawer(service)}
                      className="text-white underline text-sm"
                    >
                      {t('servicesTable.viewDetails')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServicesTable;
